import React, { PureComponent, Component, PropTypes } from 'react';
import classnames from 'classnames';
import Mediator from '../../services/Mediator';

export const fadein = (ChildComponent) => {
    return class FadeIn extends Component {
        constructor(props, ...rest) {
            super(props, ...rest);

            this.state = {
                visible: false
            };
        }
        componentDidMount() {
            this.setState({ visible: true });
        }
        render(){
            return <ChildComponent {...this.state} {...this.props} />;
        }
    };
};

export class ClickOutside extends Component {
    constructor(props) {
        super(props);

        this.ref = null;
        this.setRefs = this.setRefs.bind(this);           
        this.handleClick = this.handleClick.bind(this);
    }
    componentDidMount() {
        document.addEventListener('mousedown', this.handleClick);
    }
    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClick);
    }
    setRefs(node) {
        this.ref = node;
    }
    handleClick(event) {
        let { active, onClick, onClickOutside } = this.props;

        if (!active && this.ref && this.ref.contains(event.target)) onClick();
        if (active && this.ref && !this.ref.contains(event.target)) onClickOutside();
    }
    render() {
        return (<span ref={ this.setRefs }>
            { this.props.children }
        </span>);
    }
};
ClickOutside.propTypes = {
    active: PropTypes.bool.isRequired,
    onClick: PropTypes.func.isRequired,
    onClickOutside: PropTypes.func.isRequired
};

const applyEventsToElement = (fnName) => (element, events) => {
    for (let eventName in events) {
        if (events[eventName] && events[eventName].constructor === Function) {
            element[fnName](eventName, events[eventName]);
        }
    }
};
const addAllEventListeners = applyEventsToElement('addEventListener');
const removeAllEventListeners = applyEventsToElement('removeEventListener');

export function dragTarget(WrappedComponent, outerClassName = null) {
    return class extends PureComponent {
        constructor(...args) {
            super(...args);

            this.el = null;
            this.hitSize = null;
            this.center = null;

            this.state = {
                hover: false,
                populated: false
            };
        }
        static propTypes = {
            dropPayload: PropTypes.any.isRequired,
            onDroppedInto: PropTypes.func.isRequired
        }
        resetAll() {
            this.hitSize = null;
            this.center = null;
            this.setState({ hover: false });
        }

        componentDidMount() {
            Mediator.add('dragger', this);
        }
        componentWillUnmount() {
            this.resetAll();
            Mediator.remove('dragger', this);
        }
        onDraggerActive() {
            this.setState({ hover: false });

            const { left, right, top, bottom } = this.el.firstChild.getBoundingClientRect();

            this.hitSize = Math.round(((right - left) + (bottom - top)) / 2);

            this.center = {
                x: Math.round(left + (right - left) / 2),
                y: Math.round(top + (bottom - top) / 2)
            };
        }
        onDraggerInactive() {
            this.resetAll();
        }
        onMove({x, y}) {
            const dist = Math.sqrt(Math.pow(this.center.x - x, 2) + Math.pow(this.center.y - y, 2));

            this.setState({
                hover: dist < this.hitSize
            });
        }
        onDrop({dragPayload, x, y}) {
            const { onDroppedInto, dropPayload } = this.props;
            const dist = Math.sqrt(Math.pow(this.center.x - x, 2) + Math.pow(this.center.y - y, 2));
            if (dist < this.hitSize) onDroppedInto({ source: dragPayload, target: dropPayload})
        }
        render() {
            const { className } = this.props;
            const { hover, populated } = this.state;
            const classes = classnames({
                [className]: true,
                hover,
                populated
            });

            return (<div ref={ node => this.el = node } className={ outerClassName }>
                <WrappedComponent {...this.props} className={ classes } ref={ node => this.el = node } />
            </div>);
        }
    };
}

export function dragSource(WrappedComponent, className = null) {
    return class extends PureComponent {
        constructor(...args) {
            super(...args);

            this.el = null;
            this.dragger = null;

            this.events = {
                mousemove: this.move.bind(this),
                mouseleave: this.stopTracking.bind(this),
                mouseup: this.drop.bind(this)
            };

            this.startTracking = this.startTracking.bind(this);
        }
        static propTypes = {
            dragPayload: PropTypes.any.isRequired
        };
        injectDragger({ pageX, pageY }) {
            this.dragger = this.el.cloneNode(true);
            this.dragger.classList.add('dragger');

            this.moveDragger({ pageX, pageY });

            this.el.parentElement.appendChild(this.dragger);

            addAllEventListeners(document.body, this.events);
        }
        moveDragger({ pageX, pageY }) {
            this.dragger.style.left = `${pageX}px`;
            this.dragger.style.top = `${pageY}px`;
        }
        removeDragger() {
            removeAllEventListeners(document.body, this.events);
            this.el.parentNode.removeChild(this.dragger);
        }

        stopTracking() {
            Mediator.broadcast('dragger:DraggerInactive');
            this.removeDragger();
        }

        move(evt) {
            const { pageX, pageY } = evt;
            evt.preventDefault();
            this.moveDragger(evt);
            Mediator.broadcast('dragger:Move', { x: pageX, y: pageY});
        }
        drop(evt) {
            const { dragPayload } = this.props;
            const {pageX: x, pageY: y} = evt;
            Mediator.broadcast('dragger:Drop', { dragPayload, x, y});
            this.stopTracking();
        }

        startTracking(evt) {
            evt.preventDefault();
            this.injectDragger(evt);
            Mediator.broadcast('dragger:DraggerActive');
        }

        render() {
            return (<div ref={ node => this.el = node } className={ className } onMouseDown={ this.startTracking }>
                <WrappedComponent {...this.props} />
            </div>);
        }
    };
}


// WEBPACK FOOTER //
// ./src/components/decorators/index.js